import { AxiosResponse } from 'axios';
import { Column } from 'primereact/column';
import { DataTable, DataTableSortEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import { RadioButton } from 'primereact/radiobutton';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DeleteConfirmationDialog from '../../../components/delete-confirmation';
import Loader from '../../../components/loader';
import PrimePaginator from '../../../components/prime-paginator';
import {
    NameTrackerDDLEntity, PaginateReqEntity, PaginateRespEntity, WorkCenterDDLEntity,
    WorkflowRouteDetailsEntity, WorkflowRouteReqEntity, WorkflowStageEntity
} from '../../../interface';
import { APIResponseEntity } from '../../../interface/api-responce';
import {
    APICreateWorkflowRoute, APIDeleteWorkflowRoute, APIFetchDetailsWorkflowRoute,
    APIGetNameTrackersDDL, APIGetWorkCentersDDL, APIToExportWorkflowRoutes, APIUpdateWorkflowRoute
} from '../../../service/api-service';
import { checkActionAuthorization, displayDateFormat, downloadCsvFile } from '../../../service/shared.service';
import { maxLength100, searchInputPlaceholder, toastOptObj } from '../../../utils/constants';
import { IsNullOrEmptyArray, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import RouteFlowDiagram from '../../../components/route-flow-diagram';
declare var $: any;

/** route id shape e.g. RT-WSF (same rule as the reference prototype) */
const ROUTE_CODE_PATTERN = /^RT-[A-Z0-9-]{2,12}$/;

/** the two fixed ends of every route */
const RM_CODE = 'RM';   // Raw Material — the material gate every route starts at
const RFD_CODE = 'RFD'; // Ready for Dispatch — the FG Store closes the journey

/* ---------- stage editor styling (theme-independent, so colors always render) ---------- */

const pillButtonBase: React.CSSProperties = {
    border: '1px solid transparent',
    borderRadius: '999px',
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1.4,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(31, 41, 51, 0.08)',
};

const editorButtonStyles: Record<string, React.CSSProperties> = {
    nextStep: { ...pillButtonBase, background: '#E1F5F4', borderColor: '#A9DEDA', color: '#17766E' },
    parallelLeg: { ...pillButtonBase, background: '#E8ECFB', borderColor: '#C9D3F2', color: '#1F3BB3' },
    stageAfter: { ...pillButtonBase, background: '#FFFFFF', borderColor: '#D6DBE1', color: '#52606D' },
    removeStage: { ...pillButtonBase, background: '#FBE9E9', borderColor: '#F2C9C9', color: '#D64545' },
    addStage: { ...pillButtonBase, background: '#1F3BB3', borderColor: '#1F3BB3', color: '#FFFFFF', padding: '7px 18px' },
    removeStep: { border: 'none', background: 'transparent', color: '#D64545', fontWeight: 800, cursor: 'pointer', padding: '0 4px' },
};

const stageChip = (background: string, color: string): React.CSSProperties => ({
    display: 'inline-block',
    background,
    color,
    borderRadius: '999px',
    padding: '2px 10px',
    fontSize: '10.5px',
    fontWeight: 800,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
});

/**
 * Workflow Routes master.
 *
 * Route = route id + name + stages (work centers per position; more
 * than one per stage = parallel; the next single stage is the merge).
 * Every route starts at RM and ends at RFD. Routes are edited in a
 * modal.
 *
 * Route mapping lives on the masters themselves — the Model master
 * (per punch item), Panel Only and Accessory Only (single route each).
 */
function WorkflowRoutes() {

    const [isLoading, setIsLoading] = useState<boolean>(false);

    /* ---------- routes table state ---------- */

    const [routeDetails, setRouteDetails] = useState<WorkflowRouteDetailsEntity[] | []>([]);
    const [routeTotalCount, setRouteTotalCount] = useState<number>(0);
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [isDeleteRouteId, setIsDeleteRouteId] = useState<string>('');

    const [routeFilterReq, setRouteFilterReq] = useState<PaginateReqEntity>({
        pageSize: 10,
        pageNumber: 0,
        searchText: '',
        sortColumn: 'createdAt',
        sortOrder: '-1',
        status: ''
    });

    /* ---------- route editor modal state ---------- */

    const [showRouteDialog, setShowRouteDialog] = useState<boolean>(false);
    const [editRouteId, setEditRouteId] = useState<string>('');   // empty = create

    const [routeCodeFV, setRouteCodeFV] = useState<string>('');
    const [routeNameFV, setRouteNameFV] = useState<string>('');
    const [routeIsActive, setRouteIsActive] = useState<string>("true");
    const [dialogStages, setDialogStages] = useState<WorkflowStageEntity[]>([]);
    const [isDialogSubmitted, setIsDialogSubmitted] = useState<boolean>(false);

    const [workCentersDDL, setWorkCentersDDL] = useState<WorkCenterDDLEntity[] | []>([]);
    const [nameTrackersDDL, setNameTrackersDDL] = useState<NameTrackerDDLEntity[] | []>([]);

    // route shown in the read-only "view mapping" dialog (null = closed)
    const [viewRoute, setViewRoute] = useState<WorkflowRouteDetailsEntity | null>(null);

    const Status = [
        { name: 'Active', code: "true" },
        { name: 'Inactive', code: "false" },
    ];

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            await fetchWorkflowRoutes();
        };
        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeFilterReq]);

    useEffect(() => {
        const fetchDDLs = async (): Promise<void> => {
            await fetchWorkCentersDDL();
            await fetchNameTrackersDDL();
        };
        fetchDDLs();

        return () => {
            toast.dismiss();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        $("#id_of_searchbox").keyup((event: any) => {
            const searchText: string = IsStringNullEmptyOrUndefined(event.target.value) ? '' : event.target.value;

            if (event.keyCode === 13 &&
                (
                    (IsStringNullEmptyOrUndefined(searchText))
                    ||
                    (!IsStringNullEmptyOrUndefined(searchText) && searchText.length >= 2)
                )
            ) {
                setRouteFilterReq({ ...routeFilterReq, searchText, pageNumber: 0 });
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------- fetchers ---------- */

    const fetchWorkflowRoutes = async (): Promise<void> => {
        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIFetchDetailsWorkflowRoute(routeFilterReq);
        setIsLoading(false);
        setRouteDetails([]);
        setRouteTotalCount(0);

        if (resp) {
            if (resp.data && resp.status === 200) {
                const axiosResp: APIResponseEntity = resp.data;
                if (axiosResp && axiosResp.data && axiosResp.status && axiosResp.statusCode === 200) {
                    const paginateResp: PaginateRespEntity = axiosResp.data;
                    setRouteDetails(paginateResp.data);
                    setRouteTotalCount(paginateResp.totalCount);
                }
            } else {
                toast.error(resp.data.message, { ...toastOptObj });
            }
        }
    };

    const fetchWorkCentersDDL = async (): Promise<void> => {
        const resp: AxiosResponse<APIResponseEntity> = await APIGetWorkCentersDDL();
        if (resp && resp.data && resp.status === 200) {
            const axiosResp: APIResponseEntity = resp.data;
            if (axiosResp && axiosResp.data && axiosResp.status && axiosResp.statusCode === 200) {
                setWorkCentersDDL(axiosResp.data.data || []);
            }
        }
    };

    const fetchNameTrackersDDL = async (): Promise<void> => {
        const resp: AxiosResponse<APIResponseEntity> = await APIGetNameTrackersDDL();
        if (resp && resp.data && resp.status === 200) {
            const axiosResp: APIResponseEntity = resp.data;
            if (axiosResp && axiosResp.data && axiosResp.status && axiosResp.statusCode === 200) {
                setNameTrackersDDL(axiosResp.data.data || []);
            }
        }
    };

    /* ---------- work center / named track helpers ---------- */

    const workCenterIdByCode = (code: string): string => {
        const workCenter = workCentersDDL.find((wc) => wc.code === code);
        return workCenter ? workCenter.id : '';
    };

    const workCenterCodeById = (workCenterId: string): string => {
        const workCenter = workCentersDDL.find((wc) => wc.id === workCenterId);
        return workCenter ? workCenter.code : '?';
    };

    const nameTrackerById = (nameTrackerId: string): NameTrackerDDLEntity | undefined => {
        return nameTrackersDDL.find((tracker) => tracker.id === nameTrackerId);
    };

    /**
     * The step picker's options — TWO flat groups, same as the reference:
     * "Work Centres" (pick one, adds a single step) and "Named Tracks"
     * (pick one, expands into its whole chain of steps in one go — see
     * `changeStepGroup`). Used by PrimeReact's Dropdown via
     * optionGroupLabel/optionGroupChildren.
     */
    const groupedStepPickerOptions = (): { label: string; items: { id: string; label: string }[] }[] => {
        const workCenterItems = workCentersDDL.map((wc) => ({ id: wc.id, label: `${wc.name} (${wc.code})` }));
        const nameTrackerItems = nameTrackersDDL.map((tracker) => {
            // preview the track's expanded chain, e.g. "ALUCUT→PHD→ASSEMBLY", so
            // it's clear what a track inserts BEFORE picking it (same idea as the
            // reference's "expands" column)
            const expandedCodes = tracker.workCenterIds.map((workCenterId) => workCenterCodeById(workCenterId)).join('→');
            return { id: tracker.id, label: `▤ ${tracker.name} (${tracker.trackerCode}) — ${expandedCodes}` };
        });

        return nameTrackerItems.length > 0
            ? [{ label: 'Work Centres', items: workCenterItems }, { label: 'Named Tracks', items: nameTrackerItems }]
            : [{ label: 'Work Centres', items: workCenterItems }];
    };

    /**
     * Groups a leg's raw, fully-expanded `workCenterIds` into UI chips: a
     * run of consecutive ids that belong to the SAME `trackUsages` entry
     * (i.e. all came from ONE track pick) collapses into a single chip; a
     * manually-picked id (not covered by any usage) is its own chip of
     * length 1. `trackUsages` records ONE entry per track SELECTION (not
     * one per expanded step), so this is where the per-step lookup gets
     * (re)derived for display — the underlying data never changes.
     */
    const groupedStepsOfEntry = (entry: { workCenterIds: string[]; trackUsages?: { nameTrackerId: string; workCenterIds: string[] }[]; }): { startIndex: number; length: number; nameTrackerId: string; }[] => {
        const nameTrackerIdByWorkCenterId = new Map<string, string>();
        (entry.trackUsages || []).forEach((usage) => usage.workCenterIds.forEach((id) => nameTrackerIdByWorkCenterId.set(id, usage.nameTrackerId)));

        const groups: { startIndex: number; length: number; nameTrackerId: string; }[] = [];

        entry.workCenterIds.forEach((workCenterId, index) => {
            const nameTrackerId = nameTrackerIdByWorkCenterId.get(workCenterId) || '';
            const lastGroup = groups[groups.length - 1];
            const continuesLastGroup = lastGroup && nameTrackerId && lastGroup.nameTrackerId === nameTrackerId
                && lastGroup.startIndex + lastGroup.length === index;

            if (continuesLastGroup) {
                lastGroup.length += 1;
            } else {
                groups.push({ startIndex: index, length: 1, nameTrackerId });
            }
        });

        return groups;
    };

    /* ---------- route editor modal ---------- */

    const openCreateRouteDialog = (): void => {
        setEditRouteId('');
        setRouteCodeFV('');
        setRouteNameFV('');
        setRouteIsActive("true");
        // every route starts at RM and ends at RFD — prefill both ends
        setDialogStages([
            { entries: [{ workCenterIds: [workCenterIdByCode(RM_CODE)], trackUsages: [] }] },
            { entries: [{ workCenterIds: [workCenterIdByCode(RFD_CODE)], trackUsages: [] }] }
        ]);
        setIsDialogSubmitted(false);
        setShowRouteDialog(true);
    };

    const openEditRouteDialog = (rowData: WorkflowRouteDetailsEntity): void => {
        // the system default route (error / extra work orders) is locked on the backend too
        if (rowData.isDefault) { return; }

        setEditRouteId(rowData.id);
        setRouteCodeFV(rowData.routeCode || '');
        setRouteNameFV(rowData.name || '');
        setRouteIsActive(String(rowData.isActive));
        // stored shape keeps a track's ids ONLY inside its trackUsages entry;
        // the editor works on the leg's FULL path, so rebuild it here:
        // track chains first (in pick order), then the individually-picked ids.
        // Filter track-covered ids out of workCenterIds defensively — if the
        // API ever lists a track-expanded id in BOTH places, the editor would
        // otherwise show (and validate) that work center twice
        setDialogStages(rowData.stages.map((stage) => ({
            entries: stage.entries.map((entry) => {
                const trackCoveredIds = new Set((entry.trackUsages || []).flatMap((usage) => usage.workCenterIds));
                return {
                    workCenterIds: [
                        ...(entry.trackUsages || []).flatMap((usage) => usage.workCenterIds),
                        ...(entry.workCenterIds || []).filter((workCenterId) => !trackCoveredIds.has(workCenterId))
                    ],
                    trackUsages: (entry.trackUsages || []).map((usage) => ({ ...usage, workCenterIds: [...usage.workCenterIds] }))
                };
            })
        })));
        setIsDialogSubmitted(false);
        setShowRouteDialog(true);
    };

    /* stage builder actions — an entry = one LEG holding ordered steps */

    const addStageAfter = (stageIndex: number): void => {
        const updated = [...dialogStages];
        updated.splice(stageIndex + 1, 0, { entries: [{ workCenterIds: [''], trackUsages: [] }] });
        setDialogStages(updated);
    };

    // inserts just BEFORE the last stage — the route always ends at RFD
    const addStageAtEnd = (): void => {
        const updated = [...dialogStages];
        updated.splice(Math.max(updated.length - 1, 0), 0, { entries: [{ workCenterIds: [''], trackUsages: [] }] });
        setDialogStages(updated);
    };

    const removeStage = (stageIndex: number): void => {
        setDialogStages(dialogStages.filter((_, index) => index !== stageIndex));
    };

    const addParallelEntry = (stageIndex: number): void => {
        const updated = [...dialogStages];
        updated[stageIndex] = { entries: [...updated[stageIndex].entries, { workCenterIds: [''], trackUsages: [] }] };
        setDialogStages(updated);
    };

    // chains the NEXT work center inside a leg (e.g. ALUCUT -> PHD -> ASSEMBLY)
    const addStep = (stageIndex: number, entryIndex: number): void => {
        const updated = [...dialogStages];
        const entries = [...updated[stageIndex].entries];
        entries[entryIndex] = {
            workCenterIds: [...entries[entryIndex].workCenterIds, ''],
            trackUsages: entries[entryIndex].trackUsages || []
        };
        updated[stageIndex] = { entries };
        setDialogStages(updated);
    };

    /**
     * The step picker offers Work Centres (one item = one step) and Named
     * Tracks (one item = a whole pre-built chain, e.g. TRK-ALU = ALUCUT ->
     * PHD). Picking a work center just fills this one slot; picking a
     * track EXPANDS it and replaces this one slot with its full resolved
     * chain, tagging every inserted step with that track's id.
     */
    const changeStepGroup = (stageIndex: number, entryIndex: number, startIndex: number, length: number, selectedId: string): void => {
        const tracker = nameTrackerById(selectedId);

        if (tracker && IsNullOrEmptyArray(tracker.workCenterIds)) {
            toast.error(`${tracker.trackerCode} has no work centers configured.`, { ...toastOptObj });
            return;
        }

        const insertedIds = tracker ? tracker.workCenterIds : [selectedId];

        // rule: no work center can appear twice across the route. Block the
        // pick right here instead of failing at save — this also matters for
        // the editor itself, because a step's track chip is identified BY its
        // work center id: a duplicate id (e.g. picking ALUCUT manually when a
        // selected track already contains ALUCUT) would get wrongly absorbed
        // into / rendered as that track's chip
        const usedElsewhere = new Set<string>();
        dialogStages.forEach((stage, sIndex) => {
            stage.entries.forEach((stageEntry, eIndex) => {
                stageEntry.workCenterIds.forEach((workCenterId, wIndex) => {
                    const isReplacedSlot = sIndex === stageIndex && eIndex === entryIndex
                        && wIndex >= startIndex && wIndex < startIndex + length;
                    if (!isReplacedSlot && !IsStringNullEmptyOrUndefined(workCenterId)) usedElsewhere.add(workCenterId);
                });
            });
        });

        const duplicateId = insertedIds.find((workCenterId) => usedElsewhere.has(workCenterId));
        if (duplicateId) {
            toast.error(
                tracker
                    ? `${tracker.trackerCode} contains work center ${workCenterCodeById(duplicateId)}, which is already used in this route.`
                    : `Work center ${workCenterCodeById(duplicateId)} is already used in this route.`,
                { ...toastOptObj }
            );
            return;
        }

        const updated = [...dialogStages];
        const entries = [...updated[stageIndex].entries];
        const workCenterIds = [...entries[entryIndex].workCenterIds];
        workCenterIds.splice(startIndex, length, ...insertedIds);

        // a usage stays valid only while ALL of its ids are still present —
        // this drops the group being replaced, and any usage that lost a
        // member to some earlier edit, in one pass; no index bookkeeping needed
        let trackUsages = (entries[entryIndex].trackUsages || [])
            .filter((usage) => usage.workCenterIds.every((id) => workCenterIds.includes(id)));

        if (tracker) trackUsages = [...trackUsages, { nameTrackerId: tracker.id, workCenterIds: insertedIds }];

        entries[entryIndex] = { workCenterIds, trackUsages };
        updated[stageIndex] = { entries };
        setDialogStages(updated);
    };

    // removes a whole group at once (a lone manually-picked step is just a group of length 1)
    const removeStepGroup = (stageIndex: number, entryIndex: number, startIndex: number, length: number): void => {
        const updated = [...dialogStages];
        const entries = [...updated[stageIndex].entries];

        const inGroup = (index: number): boolean => index >= startIndex && index < startIndex + length;
        const workCenterIds = entries[entryIndex].workCenterIds.filter((_, index) => !inGroup(index));
        const trackUsages = (entries[entryIndex].trackUsages || [])
            .filter((usage) => usage.workCenterIds.every((id) => workCenterIds.includes(id)));

        if (IsNullOrEmptyArray(workCenterIds)) {
            // last group removed -> drop the whole leg
            const remainingEntries = entries.filter((_, index) => index !== entryIndex);
            if (IsNullOrEmptyArray(remainingEntries)) {
                // last leg removed -> drop the whole stage
                setDialogStages(dialogStages.filter((_, index) => index !== stageIndex));
            } else {
                updated[stageIndex] = { entries: remainingEntries };
                setDialogStages(updated);
            }
        } else {
            entries[entryIndex] = { workCenterIds, trackUsages };
            updated[stageIndex] = { entries };
            setDialogStages(updated);
        }
    };

    /* validation — mirrors the backend / reference rules */

    const validateRouteDialog = (): string => {
        if (IsStringNullEmptyOrUndefined(editRouteId) && !ROUTE_CODE_PATTERN.test(routeCodeFV.toUpperCase())) {
            return 'Route id shape must be RT-XXX (e.g. RT-WSF).';
        }

        if (IsStringNullEmptyOrUndefined(routeNameFV) || routeNameFV.trim().length < 3) {
            return 'Please enter route name (minimum 3 characters).';
        }

        if (IsNullOrEmptyArray(dialogStages)) return 'Route needs at least one stage.';

        const seen: string[] = [];

        for (let i = 0; i < dialogStages.length; i++) {
            const stage = dialogStages[i];

            for (const entry of stage.entries) {
                for (const stepWorkCenterId of entry.workCenterIds) {
                    if (IsStringNullEmptyOrUndefined(stepWorkCenterId)) return `Please select a work center for stage ${i + 1}.`;

                    if (seen.includes(stepWorkCenterId)) {
                        return `Work center ${workCenterCodeById(stepWorkCenterId)} appears twice across the route.`;
                    }
                    seen.push(stepWorkCenterId);
                }
            }

            // a parallel stage must be followed by a SINGLE leg — all
            // parallel legs merge at that leg's first work center; the
            // leg may then chain further steps (e.g. DHO -> GLAZING)
            if (stage.entries.length > 1) {
                const next = dialogStages[i + 1];
                if (!next || next.entries.length !== 1) {
                    return `Stage ${i + 1} is parallel — the next stage must be a single leg (the merge point).`;
                }
            }
        }

        return '';
    };

    // dialog stages enriched with codes so the live flow diagram can draw them
    const dialogStagesForDiagram = (): WorkflowStageEntity[] => {
        return dialogStages.map((stage) => ({
            entries: stage.entries.map((entry) => ({
                workCenterIds: entry.workCenterIds,
                steps: entry.workCenterIds.map((stepWorkCenterId) => ({
                    workCenterId: stepWorkCenterId,
                    workCenterCode: IsStringNullEmptyOrUndefined(stepWorkCenterId) ? '?' : workCenterCodeById(stepWorkCenterId),
                }))
            }))
        }));
    };

    // readable preview like: RM → [ ALUCUT→PHD→ASSEMBLY ∥ GHO ] → GLAZING → PDI → RFD
    const routeDialogPreview = (): string => {
        return dialogStages
            .map((stage) => {
                const legs = stage.entries
                    .map((entry) => entry.workCenterIds
                        .filter((stepWorkCenterId) => !IsStringNullEmptyOrUndefined(stepWorkCenterId))
                        .map((stepWorkCenterId) => workCenterCodeById(stepWorkCenterId))
                        .join('→'))
                    .filter((leg) => !IsStringNullEmptyOrUndefined(leg));
                if (IsNullOrEmptyArray(legs)) return '';
                return legs.length > 1 ? `[ ${legs.join(' ∥ ')} ]` : legs[0];
            })
            .filter((label) => !IsStringNullEmptyOrUndefined(label))
            .join(' → ');
    };

    const saveRoute = async (): Promise<void> => {
        setIsDialogSubmitted(true);

        const error: string = validateRouteDialog();
        if (!IsStringNullEmptyOrUndefined(error)) {
            toast.error(error, { ...toastOptObj });
            return;
        }

        // the editor works on each leg's FULL path; the SAVED shape keeps a
        // track's ids only inside its trackUsages entry, so strip every
        // track-covered id out of workCenterIds before sending
        const stagesForSave: WorkflowStageEntity[] = dialogStages.map((stage) => ({
            entries: stage.entries.map((entry) => {
                const trackCoveredIds = new Set((entry.trackUsages || []).flatMap((usage) => usage.workCenterIds));
                return {
                    workCenterIds: entry.workCenterIds.filter((workCenterId) => !trackCoveredIds.has(workCenterId)),
                    trackUsages: entry.trackUsages || []
                };
            })
        }));

        const reqObj: WorkflowRouteReqEntity = {
            routeCode: routeCodeFV.toUpperCase(),
            name: routeNameFV.trim(),
            stages: stagesForSave,
            isActive: routeIsActive == "true"
        };

        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = !IsStringNullEmptyOrUndefined(editRouteId)
            ? await APIUpdateWorkflowRoute(reqObj, editRouteId)
            : await APICreateWorkflowRoute(reqObj);
        setIsLoading(false);

        if (resp) {
            if (resp.data && (resp.status === 200 || resp.status === 201)) {
                const axiosResp: APIResponseEntity = resp.data;
                if (axiosResp && axiosResp.status && (axiosResp.statusCode === 200 || axiosResp.statusCode === 201)) {
                    toast.success(axiosResp.message, { ...toastOptObj });
                    setShowRouteDialog(false);
                    setRouteFilterReq({ ...routeFilterReq, pageNumber: 0 });
                } else {
                    toast.error(axiosResp.message, { ...toastOptObj });
                }
            } else {
                toast.error(resp.data.message, { ...toastOptObj });
            }
        }
    };

    /* ---------- route actions ---------- */

    // exports all routes to XLSX — the flow text of every route plus a
    // stage-by-stage "Route Diagram" sheet (the SVG itself can't live in Excel)
    const exportWorkflowRoutes = async (): Promise<void> => {
        setIsLoading(true);
        try {
            const resp: AxiosResponse<APIResponseEntity> = await APIToExportWorkflowRoutes();
            if (resp && resp.data && resp.status === 200) {
                const axiosResp: APIResponseEntity = resp.data;
                if (axiosResp && axiosResp.data && axiosResp.status && axiosResp.statusCode === 200) {
                    const excelUrl = axiosResp.data.xlsxUrl;
                    if (excelUrl && !IsStringNullEmptyOrUndefined(excelUrl)) {
                        const fileName: string = excelUrl.split('/').pop();
                        if (fileName && !IsStringNullEmptyOrUndefined(fileName)) {
                            await downloadCsvFile(excelUrl, fileName);
                        }
                    }
                } else {
                    toast.error(axiosResp.message, { ...toastOptObj });
                }
            } else {
                toast.error(resp?.data?.message, { ...toastOptObj });
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const deleteWorkflowRoute = async (value: boolean) => {
        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIDeleteWorkflowRoute(isDeleteRouteId);
        setIsLoading(false);
        if (resp && resp.data && resp.status === 200) {
            const axiosResp: APIResponseEntity = resp.data;
            if (axiosResp && axiosResp.status && axiosResp.statusCode === 200) {
                toast.success(axiosResp.message, { ...toastOptObj });
                setRouteFilterReq({ ...routeFilterReq, pageNumber: 0 });
            } else {
                toast.error(axiosResp.message, { ...toastOptObj });
            }
        } else if (resp) {
            toast.error(resp.data.message, { ...toastOptObj });
        }
    };

    /* ---------- table templates ---------- */

    const statusBodyTemplate = (rowData: { isActive: boolean }) => {
        return <span className={`badge badge-${rowData.isActive === true ? "success" : "danger"}`}> {rowData.isActive ? "Active" : "Inactive"}</span>;
    };

    const routeDateBodyTemplate = (rowData: WorkflowRouteDetailsEntity) => {
        return displayDateFormat(rowData.createdAt);
    };

    const routeActionTemplate = (rowData: WorkflowRouteDetailsEntity) => {
        return (
            <ul className="tableIcon cursor-pointer">
                <li data-toggle="tooltip" title="View Route Mapping">
                    <img src="assets/images/eye-icon.svg" alt="View" onClick={() => setViewRoute(rowData)} />
                </li>
                {rowData.isDefault &&
                    <li data-toggle="tooltip" title="System default route for error / extra work orders — locked">
                        <i className="pi pi-lock" />
                    </li>
                }
                {!rowData.isDefault && checkActionAuthorization('update', 'Workflow Routes') &&
                    <li data-toggle="tooltip" title="Edit Stages">
                        <img src="assets/images/edit-icon.svg" alt="Edit" onClick={() => openEditRouteDialog(rowData)} />
                    </li>
                }
                {!rowData.isDefault && checkActionAuthorization('delete', 'Workflow Routes') &&
                    <li data-toggle="tooltip" title="Delete" onClick={() => {
                        setIsDeleteRouteId(rowData.id);
                    }}>
                        <DeleteConfirmationDialog
                            isAccepted={deleteWorkflowRoute}
                        />
                    </li>}
            </ul>
        );
    };

    const statusRowFilterTemplate = () => {
        return (
            <Dropdown
                value={routeFilterReq.status}
                options={Status}
                optionLabel="name"
                optionValue="code"
                onChange={(e) => {
                    setRouteFilterReq({
                        ...routeFilterReq,
                        status: e.value,
                        pageNumber: 0
                    });
                }}
                placeholder="Select status"
                className="p-column-filter"
            />
        );
    };

    const resetFilters = (): void => {
        setSearchKeyword('');

        setRouteFilterReq({
            ...routeFilterReq,
            searchText: '',
            status: '',
            pageNumber: 0
        });
    };
    /* ---------- paginator / sort ---------- */

    const onRoutePageChange = (event: PaginatorPageChangeEvent) => {
        setRouteFilterReq({ ...routeFilterReq, pageSize: event.rows, pageNumber: event.page });
    };

    const onRouteSort = (event: DataTableSortEvent) => {
        setRouteFilterReq({ ...routeFilterReq, sortColumn: event.sortField, sortOrder: String(event.sortOrder), pageNumber: 0 });
    };

    /* ---------- route editor dialog ---------- */

    const routeDialogFooter = (
        <div>
            <button
                type="button"
                className="btn btn-cancel text-dark m-0 me-2"
                onClick={() => setShowRouteDialog(false)}>
                Cancel
            </button>
            <button
                type="button"
                className="btn btn-primary btn-md font-weight-medium m-0"
                onClick={saveRoute}>
                {IsStringNullEmptyOrUndefined(editRouteId) ? 'Create Route' : 'Save Route'}
            </button>
        </div>
    );

    return (
        <>
            <Loader isLoading={isLoading} />

            <div className="content-wrapper">
                <div className='col-12'>

                    {/* ---------- workflow routes ---------- */}
                    <div className="card card-rounded mb-4">
                        <div className='card-body'>
                            <div className='row align-items-center mb-3'>
                                <div className="col-md-5">
                                    <h4 className="card-title mb-0">Workflow Routes</h4>
                                </div>
                                <div className="col-md-5">
                                    <div className="input-group search-grp">
                                        <img src="assets/images/search.svg"
                                            alt="search" className="input-group-text searchIptGrpTxt" />

                                        <input type="text" className='form-control form-control-lg searchInput'
                                            placeholder={searchInputPlaceholder}
                                            value={searchKeyword} id="id_of_searchbox"
                                            onChange={(e) => setSearchKeyword(e.target.value)}
                                        />
                                        {!IsStringNullEmptyOrUndefined(searchKeyword) && (
                                            <button
                                                className="btn btn-cancel text-dark search-button-clear"
                                                type="button"
                                                onClick={() => {
                                                    setSearchKeyword('');
                                                    setRouteFilterReq({ ...routeFilterReq, searchText: '', pageNumber: 0 });
                                                }}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                                        {checkActionAuthorization('upgrade', 'Allow to Export Workflow Routes') &&
                                            <button
                                                type="button"
                                                className="btn btn-success btn-md font-weight-medium m-0"
                                                onClick={exportWorkflowRoutes}>
                                                Export
                                            </button>
                                        }
                                        {checkActionAuthorization('create', 'Workflow Routes') &&
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-md font-weight-medium m-0"
                                                onClick={openCreateRouteDialog}>
                                                + New Route
                                            </button>
                                        }
                                    </div>
                                </div>
                                <div className=" d-flex justify-content-end mt-2"                                                >
                                    <span className="resetFilters text-primary cursor-pointer " onClick={resetFilters}> Reset Filters  </span>
                                </div>
                            </div>

                            <div className="col-md-12">
                                <DataTable
                                    value={routeDetails}
                                    sortField={routeFilterReq.sortColumn}
                                    sortOrder={routeFilterReq.sortOrder
                                        ? (parseInt(routeFilterReq.sortOrder) as 1 | -1)
                                        : -1}
                                    onSort={onRouteSort}
                                    filterDisplay="row"
                                >
                                    <Column field="routeCode" header="Route Id" sortable></Column>
                                    <Column field="name" header="Route Name" sortable></Column>
                                    <Column
                                        field="isActive"
                                        header="Status"
                                        filter
                                        showFilterMenu={false}
                                        filterElement={statusRowFilterTemplate}
                                        body={statusBodyTemplate}
                                    />
                                    <Column field="createdAt" header="Date Created" body={routeDateBodyTemplate} sortable></Column>
                                    <Column header="Action" body={routeActionTemplate}></Column>
                                </DataTable>
                            </div>

                            {
                                !IsNullOrEmptyArray(routeDetails) && <PrimePaginator
                                    onPageChange={onRoutePageChange}
                                    pageNumber={routeFilterReq.pageNumber}
                                    pageSize={routeFilterReq.pageSize}
                                    totalRecords={routeTotalCount}
                                />
                            }
                        </div>
                    </div>

                    {/* ---------- route flow diagrams — one card per route ---------- */}
                    {routeDetails.map((route) => (
                        <div className="card card-rounded mt-4" key={route.id}>
                            <div className='card-body'>
                                <div className='d-flex align-items-center flex-wrap mb-2' style={{ gap: '10px' }}>
                                    <h5 className='mb-0 font-weight-bold'>{route.routeCode} · {route.name}</h5>
                                    <div className='ms-auto'>
                                        {route.isDefault
                                            ? <i className="pi pi-lock text-muted" title="System default route for error / extra work orders — locked" />
                                            : checkActionAuthorization('update', 'Workflow Routes') &&
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm m-0"
                                                onClick={() => openEditRouteDialog(route)}>
                                                Edit Stages
                                            </button>
                                        }
                                    </div>
                                </div>
                                <RouteFlowDiagram stages={route.stages} />
                            </div>
                        </div>
                    ))}

                </div>
            </div>

            {/* ---------- route editor modal (create / edit stages) ---------- */}
            <Dialog
                header={IsStringNullEmptyOrUndefined(editRouteId) ? 'New Route' : `Edit Stages — ${routeCodeFV}`}
                visible={showRouteDialog}
                style={{ width: '1200px', maxWidth: '100vw', height: '650px', maxHeight: "100vw" }}
                draggable={false}
                resizable={false}
                footer={routeDialogFooter}
                onHide={() => setShowRouteDialog(false)}
            >
                <div className='row mt-1'>
                    <div className="col-md-4">
                        <div className="form-group">
                            <label>ROUTE ID <sup className='mandatory'>*</sup></label>
                            <input
                                type="text"
                                className="form-control form-control-lg text-uppercase"
                                placeholder='RT-XXX'
                                value={routeCodeFV}
                                maxLength={15}
                                disabled={!IsStringNullEmptyOrUndefined(editRouteId)}
                                onChange={(e) => setRouteCodeFV(e.target.value.toUpperCase())}
                            />
                            {
                                isDialogSubmitted && IsStringNullEmptyOrUndefined(editRouteId) && !ROUTE_CODE_PATTERN.test(routeCodeFV)
                                &&
                                <small className='text-danger font-weight-500'>Route id shape must be RT-XXX (e.g. RT-WSF)</small>
                            }
                        </div>
                    </div>
                    <div className="col-md-5">
                        <div className="form-group">
                            <label>ROUTE NAME <sup className='mandatory'>*</sup></label>
                            <input
                                type="text"
                                className="form-control form-control-lg"
                                placeholder='e.g. Swing flush default route for location'
                                value={routeNameFV}
                                maxLength={maxLength100}
                                onChange={(e) => setRouteNameFV(e.target.value)}
                            />
                            {
                                isDialogSubmitted && (IsStringNullEmptyOrUndefined(routeNameFV) || routeNameFV.trim().length < 3)
                                &&
                                <small className='text-danger font-weight-500'>Please enter route name (minimum 3 characters)</small>
                            }
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-group">
                            <label>STATUS</label>
                            <div className='d-flex mt-2'>
                                {Status.map((option: any) => (
                                    <div key={option.code} className="d-flex align-items-center">
                                        <RadioButton
                                            inputId={`route-${String(option.code)}`}
                                            name="routeIsActive"
                                            className='me-2'
                                            value={option.code}
                                            onChange={(e) => setRouteIsActive(e.value)}
                                            checked={routeIsActive === option.code}
                                        />
                                        <label className='me-4 mb-0'>{option.name}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='row'>
                    <div className="col-12">
                        <label className='mb-0'>STAGES <sup className='mandatory'>*</sup></label>

                        {dialogStages.map((stage, stageIndex) => {
                            const isFirstStage = stageIndex === 0;                              // RM — the material gate
                            const isLastStage = stageIndex === dialogStages.length - 1;        // RFD — the FG Store
                            const isMergeStage = stageIndex > 0 && dialogStages[stageIndex - 1].entries.length > 1 && stage.entries.length === 1;

                            return (
                                <div key={stageIndex} className="card mb-2" style={{ border: '1px solid #dee2e6', borderRadius: '12px' }}>
                                    <div className="card-body py-3">
                                        <div className='d-flex align-items-start flex-wrap' style={{ gap: '10px' }}>
                                            <div style={{ minWidth: '125px', paddingTop: '6px' }}>
                                                <b>Stage {stageIndex + 1}</b>
                                                <div className='mt-1 d-flex flex-column align-items-start' style={{ gap: '3px' }}>
                                                    {isFirstStage && <span style={stageChip('#EEF1F4', '#52606D')}>START · RM GATE</span>}
                                                    {isLastStage && <span style={stageChip('#EEF1F4', '#52606D')}>END · FG STORE</span>}
                                                    {stage.entries.length > 1 && <span style={stageChip('#E8ECFB', '#1F3BB3')}>PARALLEL × {stage.entries.length} LEGS</span>}
                                                    {isMergeStage && <span style={stageChip('#E3F2EA', '#2F855A')}>MERGE POINT</span>}
                                                </div>
                                            </div>

                                            {/* one BOX per LEG — its steps chain left to right and stay inside the box */}
                                            <div className='d-flex flex-column' style={{ gap: '6px', flex: 1, minWidth: '320px' }}>
                                                {stage.entries.map((entry, entryIndex) => (
                                                    <React.Fragment key={entryIndex}>
                                                        {entryIndex > 0 &&
                                                            <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em', paddingLeft: '10px', color: '#1F3BB3' }}>
                                                                ∥ RUNS IN PARALLEL WITH
                                                            </div>
                                                        }
                                                        <div className='d-flex align-items-center flex-wrap'
                                                            style={{
                                                                gap: '6px', padding: '8px 10px', borderRadius: '10px',
                                                                border: stage.entries.length > 1 ? '1.5px dashed #8FA4E8' : '1px solid #E9ECEF',
                                                                background: stage.entries.length > 1 ? '#F7F9FD' : '#F8F9FA'
                                                            }}>
                                                            {stage.entries.length > 1 &&
                                                                <span style={stageChip('#1F3BB3', '#FFFFFF')}>LEG {entryIndex + 1}</span>
                                                            }
                                                            {groupedStepsOfEntry(entry).map((group, groupIndex) => {
                                                                // a track group's dropdown VALUE is the track's own id (so it
                                                                // keeps showing "Track Glass (TRK-GLS-G)"); a lone manual step's
                                                                // value is just that one work center's id
                                                                const dropdownValue = group.nameTrackerId || entry.workCenterIds[group.startIndex];
                                                                const expandedCodes = group.length > 1
                                                                    ? entry.workCenterIds
                                                                        .slice(group.startIndex, group.startIndex + group.length)
                                                                        .map((workCenterId) => workCenterCodeById(workCenterId))
                                                                        .join('→')
                                                                    : '';

                                                                return (
                                                                    <React.Fragment key={group.startIndex}>
                                                                        {groupIndex > 0 && <span style={{ fontWeight: 800, color: '#1F3BB3' }}>→</span>}
                                                                        {/* dropdown + remove stay glued so a line wrap never splits a group */}
                                                                        <span className='d-inline-flex flex-column' style={{ whiteSpace: 'nowrap' }}>
                                                                            <span className='d-inline-flex align-items-center' style={{ gap: '2px' }}>
                                                                                <Dropdown
                                                                                    placeholder="Select work center or named track"
                                                                                    optionValue="id"
                                                                                    value={dropdownValue}
                                                                                    options={groupedStepPickerOptions()}
                                                                                    optionGroupLabel="label"
                                                                                    optionGroupChildren="items"
                                                                                    optionLabel="label"
                                                                                    filter
                                                                                    resetFilterOnHide
                                                                                    // a track's label can be long ("Track Glass (TRK-GLS-G) — GHO→PAINT→...");
                                                                                    // cap the width and let PrimeReact's own label truncation (ellipsis) take
                                                                                    // over so the remove button next to it never gets pushed out of the box
                                                                                    style={{ maxWidth: '260px' }}
                                                                                    onChange={(e) => changeStepGroup(stageIndex, entryIndex, group.startIndex, group.length, e.value)}
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    style={editorButtonStyles.removeStep}
                                                                                    title={group.length > 1 ? 'Remove this whole track' : 'Remove work center'}
                                                                                    onClick={() => removeStepGroup(stageIndex, entryIndex, group.startIndex, group.length)}>
                                                                                    <i className="pi pi-times"></i>
                                                                                </button>
                                                                            </span>
                                                                            {/* the track's full mapped chain stays visible even though it's one chip */}
                                                                            {expandedCodes &&
                                                                                <small className='text-muted' style={{ fontSize: '10px', paddingLeft: '2px' }}>= {expandedCodes}</small>
                                                                            }
                                                                        </span>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                            <button
                                                                type="button"
                                                                style={editorButtonStyles.nextStep}
                                                                title="Add the NEXT work center of this leg (runs in sequence after the previous one)"
                                                                onClick={() => addStep(stageIndex, entryIndex)}>
                                                                → Next Step
                                                            </button>
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                            </div>

                                            <div className='ms-auto d-flex flex-wrap' style={{ gap: '6px' }}>
                                                <button
                                                    type="button"
                                                    style={editorButtonStyles.parallelLeg}
                                                    title="Add a leg that runs side by side with the legs above"
                                                    onClick={() => addParallelEntry(stageIndex)}>
                                                    ∥ Parallel Leg
                                                </button>
                                                <button
                                                    type="button"
                                                    style={editorButtonStyles.stageAfter}
                                                    title="Insert a new stage after this one"
                                                    onClick={() => addStageAfter(stageIndex)}>
                                                    + Stage After
                                                </button>
                                                {dialogStages.length > 1 &&
                                                    <button
                                                        type="button"
                                                        style={editorButtonStyles.removeStage}
                                                        onClick={() => removeStage(stageIndex)}>
                                                        Remove Stage
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <button
                            type="button"
                            style={editorButtonStyles.addStage}
                            title="Inserts a stage just before the last stage (RFD)"
                            onClick={addStageAtEnd}>
                            + Add Stage
                        </button>
                    </div>
                </div>

                {!IsStringNullEmptyOrUndefined(routeDialogPreview()) &&
                    <div className='row mt-1'>
                        <div className="col-12">
                            <div className="alert  mb-0" role="alert">
                                <b>Route preview:</b> {routeDialogPreview()}
                                <RouteFlowDiagram stages={dialogStagesForDiagram()} />
                            </div>
                        </div>
                    </div>
                }
            </Dialog>

            {/* ---------- read-only "view mapping" dialog ---------- */}
            <Dialog
                header={viewRoute ? `Route Mapping — ${viewRoute.routeCode} · ${viewRoute.name}` : ''}
                visible={viewRoute !== null}
                style={{ width: '1100px', maxWidth: '96vw' }}
                draggable={false}
                resizable={false}
                onHide={() => setViewRoute(null)}
            >
                {viewRoute &&
                    <>
                        <div className='d-flex align-items-center flex-wrap mb-2' style={{ gap: '8px' }}>
                            <span style={stageChip('#E8ECFB', '#1F3BB3')}>{viewRoute.activeMappingsCount} ACTIVE MAPPING(S)</span>
                            <span style={stageChip(viewRoute.isActive ? '#E3F2EA' : '#FBE9E9', viewRoute.isActive ? '#2F855A' : '#D64545')}>
                                {viewRoute.isActive ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                            {viewRoute.isDefault &&
                                <span style={stageChip('#FDF2DD', '#B98A35')}>DEFAULT · LOCKED</span>
                            }
                        </div>
                        <div className='text-muted mb-2' style={{ fontSize: '12.5px' }}>
                            {viewRoute.stagePreview}
                        </div>
                        <RouteFlowDiagram stages={viewRoute.stages} />
                    </>
                }
            </Dialog>
        </>
    );
}

export default WorkflowRoutes;
